/**
 * DictateSlides — Record voice → transcribe → generate slides via Pikto
 *
 * Flow:
 * 1. User clicks mic → starts recording
 * 2. User stops → uploads audio → transcribes via Imagia Whisper
 * 3. Shows transcript for review/edit
 * 4. User confirms → sends to Pikto for slide generation
 * 5. Polls job status → slides appear in presentation
 */

import { useState, useRef, useCallback } from 'react';

const API = '/api/work/documents';

export default function DictateSlides({ documentId, onSlidesReady }) {
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [step, setStep] = useState('idle'); // idle, recording, transcribing, editing, generating, done, error
  const [error, setError] = useState('');
  const [extractedTitle, setExtractedTitle] = useState('');
  const [audioDragOver, setAudioDragOver] = useState(false);
  const audioFileRef = useRef(null);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const pollRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(blob);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      setRecording(true);
      setStep('recording');
      setError('');
    } catch (err) {
      setError('Microphone access denied. Please allow microphone access.');
      setStep('error');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
      setStep('transcribing');
    }
  }, []);

  async function transcribeAudio(fileOrBlob) {
    try {
      const formData = new FormData();
      const name = fileOrBlob.name || 'dictation.webm';
      formData.append('audio', fileOrBlob, name);

      const res = await fetch(`${API}/${documentId}/transcribe-audio`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) throw new Error('Transcription failed');
      const data = await res.json();
      setTranscript(data.text || '');
      if (data.title) setExtractedTitle(data.title);
      setStep('editing');
    } catch (err) {
      setError(err.message);
      setStep('error');
    }
  }

  async function generateSlides() {
    if (!transcript.trim()) return;
    setStep('generating');
    setProgress(0);

    try {
      const res = await fetch(`${API}/${documentId}/generate-slides`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ script: transcript, title: extractedTitle || undefined }),
      });

      if (!res.ok) throw new Error('Failed to start generation');
      const data = await res.json();
      setJobId(data.jobId);

      // Poll for completion
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API}/${documentId}/generate-slides/status`, { credentials: 'include' });
          if (!statusRes.ok) return;
          const { jobs } = await statusRes.json();
          const job = jobs?.find(j => j.id === data.jobId);
          if (!job) return;

          setProgress(job.progress || 0);

          if (job.status === 'completed') {
            clearInterval(pollRef.current);
            setStep('done');
            onSlidesReady?.();
          } else if (job.status === 'failed') {
            clearInterval(pollRef.current);
            setError(job.error || 'Generation failed');
            setStep('error');
          }
        } catch {}
      }, 3000);
    } catch (err) {
      setError(err.message);
      setStep('error');
    }
  }

  function reset() {
    setStep('idle');
    setTranscript('');
    setError('');
    setJobId(null);
    setProgress(0);
    if (pollRef.current) clearInterval(pollRef.current);
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Dictate slides"
        className="px-2 sm:px-3 py-1.5 bg-purple-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-purple-700 flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
        <span className="hidden sm:inline">Dictate</span>
      </button>
    );
  }

  return (
    <>
      <button onClick={() => { setOpen(false); reset(); }} title="Close dictation"
        className="px-2 sm:px-3 py-1.5 bg-purple-100 text-purple-700 text-xs sm:text-sm font-medium rounded-lg hover:bg-purple-200 flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
        <span className="hidden sm:inline">Dictate</span>
      </button>

      {/* Floating panel */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg px-4">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-5">
          {step === 'idle' && (
            <div className="text-center"
              onDragOver={e => { e.preventDefault(); setAudioDragOver(true); }}
              onDragLeave={() => setAudioDragOver(false)}
              onDrop={e => {
                e.preventDefault(); setAudioDragOver(false);
                const file = [...(e.dataTransfer?.files || [])].find(f => f.type.startsWith('audio/'));
                if (file) { setStep('transcribing'); transcribeAudio(file); }
              }}>
              <p className="text-sm text-gray-600 mb-4">Describe your presentation and I'll generate the slides.</p>
              <div className={`flex items-center justify-center gap-4 mb-3 ${audioDragOver ? 'ring-2 ring-purple-400 rounded-xl p-4 bg-purple-50' : ''}`}>
                <button onClick={startRecording}
                  className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-transform hover:scale-105">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </button>
                {audioDragOver ? (
                  <p className="text-sm text-purple-600 font-medium">Drop audio file here</p>
                ) : (
                  <div className="text-left">
                    <input ref={audioFileRef} type="file" accept="audio/*" className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) { setStep('transcribing'); transcribeAudio(f); } }} />
                    <button onClick={() => audioFileRef.current?.click()}
                      className="text-xs text-purple-600 hover:text-purple-800 font-medium">Upload audio file</button>
                    <p className="text-[10px] text-gray-400">or drag & drop</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400">Or type your script below</p>
              <textarea value={transcript} onChange={e => setTranscript(e.target.value)}
                placeholder="Type your presentation script here..."
                className="w-full mt-2 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-purple-500 outline-none resize-none"
                rows={3} />
              {transcript.trim() && (
                <button onClick={generateSlides}
                  className="mt-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700">
                  Generate Slides
                </button>
              )}
            </div>
          )}

          {step === 'recording' && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-500 animate-pulse flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1">Recording...</p>
              <p className="text-xs text-gray-500 mb-4">Describe your presentation content</p>
              <button onClick={stopRecording}
                className="px-6 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800">
                Stop Recording
              </button>
            </div>
          )}

          {step === 'transcribing' && (
            <div className="text-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-purple-500 mx-auto mb-3" />
              <p className="text-sm text-gray-600">Transcribing your dictation...</p>
            </div>
          )}

          {step === 'editing' && (
            <div>
              {extractedTitle && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-400 mb-1">Title</p>
                  <input value={extractedTitle} onChange={e => setExtractedTitle(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold focus:ring-1 focus:ring-purple-500 outline-none" />
                </div>
              )}
              <p className="text-xs font-medium text-gray-500 mb-2">Review your script</p>
              <textarea value={transcript} onChange={e => setTranscript(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-purple-500 outline-none resize-none"
                rows={5} />
              <div className="flex justify-between mt-3">
                <button onClick={reset} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg">Re-record</button>
                <button onClick={generateSlides} disabled={!transcript.trim()}
                  className="px-4 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50">
                  Generate Slides
                </button>
              </div>
            </div>
          )}

          {step === 'generating' && (
            <div className="text-center py-2">
              <p className="text-sm font-medium text-gray-900 mb-2">Generating slides...</p>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                <div className="h-full bg-purple-600 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-gray-400">{progress}% complete</p>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-2">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1">Slides generated!</p>
              <p className="text-xs text-gray-500 mb-3">Your slides have been added to the presentation.</p>
              <button onClick={() => { setOpen(false); reset(); }}
                className="px-4 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700">Done</button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-2">
              <p className="text-sm text-red-600 mb-3">{error}</p>
              <button onClick={reset} className="px-4 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200">Try Again</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
